import { useId, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from './icons';

/** Button family: one primary action per view, secondary for alternatives, quiet for tools. */
export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger';

export function buttonClassName(variant: ButtonVariant = 'primary', extra?: string): string {
  return ['ui-button', `ui-button--${variant}`, extra].filter(Boolean).join(' ');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  icon?: IconName;
}

export function Button({ variant = 'primary', icon, className, children, ...rest }: ButtonProps) {
  return (
    <button type="button" className={buttonClassName(variant, className)} {...rest}>
      {icon && <Icon name={icon} size={20} />}
      {children}
    </button>
  );
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  /** BCP 47 tag when the option text is in another language (e.g. language endonyms). */
  lang?: string;
}

/** Native select with a visible label: keyboard, screen reader and touch behaviour for free. */
export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  icon,
  hint,
  disabled,
  compact,
}: {
  label: string;
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  icon?: IconName;
  hint?: string;
  disabled?: boolean;
  compact?: boolean;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className={compact ? 'ui-field ui-field--compact' : 'ui-field'}>
      <label className="ui-field__label" htmlFor={id}>
        {icon && <Icon name={icon} size={18} />}
        {label}
      </label>
      <select
        id={id}
        className="ui-select"
        value={value}
        disabled={disabled}
        aria-describedby={hint ? hintId : undefined}
        onChange={(e) => {
          const next = options.find((o) => o.value === e.target.value);
          if (next) onChange(next.value);
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} lang={o.lang}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && (
        <span className="ui-field__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
}

/** Multi-select filter chip; state is conveyed by aria-pressed and a check icon, not colour alone. */
export function ToggleChip({
  pressed,
  onToggle,
  children,
}: {
  pressed: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className="ui-chip" aria-pressed={pressed} onClick={onToggle}>
      {pressed && <Icon name="check" size={18} />}
      {children}
    </button>
  );
}

export type NoticeTone = 'info' | 'success' | 'warning' | 'error';

const NOTICE_ICONS: Record<NoticeTone, IconName> = {
  info: 'info',
  success: 'check',
  warning: 'alert',
  error: 'alert',
};

/**
 * Inline message with icon + text (never colour alone). `live` makes it a status (polite) or alert
 * (assertive) region; static explanatory notes use role "note".
 */
export function Notice({
  tone = 'info',
  live,
  title,
  children,
}: {
  tone?: NoticeTone;
  live?: 'polite' | 'assertive';
  title?: string;
  children: ReactNode;
}) {
  const role = live === 'assertive' ? 'alert' : live === 'polite' ? 'status' : 'note';
  return (
    <div className={`ui-notice ui-notice--${tone}`} role={role}>
      <Icon name={NOTICE_ICONS[tone]} />
      <div className="ui-notice__body">
        {title && <p className="ui-notice__title">{title}</p>}
        {children}
      </div>
    </div>
  );
}

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="ui-visually-hidden">{children}</span>;
}

/** Small factual label (format, age, availability). Pair with text; tone is a secondary cue. */
export function Tag({
  children,
  tone = 'neutral',
  icon,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'fictional' | 'game' | 'ok' | 'unavailable';
  icon?: IconName;
}) {
  return (
    <span className={`ui-tag ui-tag--${tone}`}>
      {icon && <Icon name={icon} size={16} />}
      {children}
    </span>
  );
}
